import React from "react";
import { COLORS } from "../../../../theme/colors";

// agent-experience-redesign-v1.md §6.2 + §7 (A8).
//
// Health pill rendered on each Wiring tab binding row. Four states
// match the domains that the wiring response surfaces:
//
//   healthy      — integration is reachable, credentials valid (green)
//   degraded     — soft failure, e.g. high consecutive_failures, not yet quarantined (amber)
//   no_creds     — credential_ref unset / missing tenant_settings entry (amber-red)
//   quarantined  — circuit breaker tripped (red)
//
// The pill is click-through to the credential drawer (parent passes
// onClick). Visual language matches `<Badge>` from components/ui.jsx
// for cohesion with the rest of the surface.

const STATE_STYLES = {
  healthy: {
    color: COLORS.green,
    bg: `${COLORS.green}15`,
    border: `${COLORS.green}40`,
    label: "Healthy",
  },
  degraded: {
    color: COLORS.orange,
    bg: `${COLORS.orange}15`,
    border: `${COLORS.orange}40`,
    label: "Degraded",
  },
  no_creds: {
    color: COLORS.pink || "#ef4444",
    bg: `${COLORS.pink || "#ef4444"}15`,
    border: `${COLORS.pink || "#ef4444"}40`,
    label: "No credentials",
  },
  quarantined: {
    color: COLORS.pink || "#ef4444",
    bg: `${COLORS.pink || "#ef4444"}25`,
    border: `${COLORS.pink || "#ef4444"}60`,
    label: "Quarantined",
  },
};

/**
 * Resolve a state label from the wiring-response shape.
 *   { auth_required: true,  has_credential: false }  → "no_creds"
 *   { state: "quarantined" } / "circuit_open"        → "quarantined"
 *   { consecutive_failures > 0 }                       → "degraded"
 *   default                                            → "healthy"
 *
 * Backward-compat: ``auth_required`` defaults to TRUE if missing
 * (matches the backend's fail-safe default and the banner's filter).
 * Pre-2026-05-13 the no_creds branch fired on bare ``has_credential
 * === false`` — that was correct only because the helper lied and
 * returned True for no-auth integrations. Now ``has_credential`` is
 * honest, so we have to consult ``auth_required`` to know whether
 * the missing credential MATTERS.
 */
export function resolveBindingState(row) {
  if (!row) return "healthy";
  const needsAuth = row.auth_required !== false;
  if (needsAuth && row.has_credential === false) return "no_creds";
  const s = (row.state || "").toLowerCase();
  if (s === "quarantined" || s === "circuit_open") return "quarantined";
  if ((row.consecutive_failures || 0) > 0) return "degraded";
  return "healthy";
}

export default function BindingHealthPill({ state, label, onClick, size = "md", style }) {
  const s = STATE_STYLES[state] || STATE_STYLES.healthy;
  const isSm = size === "sm";

  return (
    <button
      type="button"
      onClick={onClick}
      title={`${s.label} — click to manage`}
      aria-label={`Binding state: ${s.label}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: isSm ? "2px 8px" : "3px 10px",
        fontSize: isSm ? 10 : 11, fontWeight: 600,
        borderRadius: 999, cursor: onClick ? "pointer" : "default",
        background: s.bg,
        border: `1px solid ${s.border}`,
        color: s.color,
        transition: "all 0.18s",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      <span style={{
        width: isSm ? 6 : 7, height: isSm ? 6 : 7, borderRadius: "50%",
        background: s.color, flexShrink: 0,
        boxShadow: state === "quarantined" || state === "no_creds"
          ? `0 0 6px ${s.color}80` : "none",
      }} />
      {label || s.label}
    </button>
  );
}
